import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import ShareIcon from '@mui/icons-material/Share';
import apiService from '../services/ApiService';
import shareService from '../services/ShareService';
import LoadingSpinner from '../components/LoadingSpinner';

const CATEGORY_ORDER = ['SCORE', 'DAYS', 'STREAK', 'COMPETITION', 'WORDS'];

function AchievementCard({ achievement }) {
  const { t } = useTranslation();
  const { unlocked, unlockedAt, name, description, icon } = achievement;
  const [sharing, setSharing] = useState(false);

  const unlockedDate = unlockedAt
    ? new Date(unlockedAt).toLocaleDateString()
    : null;

  const handleShare = async (e) => {
    e.stopPropagation();
    setSharing(true);
    try {
      await shareService.shareAchievement({
        emoji: icon,
        title: name,
        subtitle: description,
      });
    } finally {
      setSharing(false);
    }
  };

  return (
    <Tooltip title={unlocked ? `${t('achievements.unlockedOn')} ${unlockedDate}` : t('achievements.locked')} arrow>
      <Card
        sx={{
          height: '100%',
          opacity: unlocked ? 1 : 0.45,
          filter: unlocked ? 'none' : 'grayscale(80%)',
          border: unlocked ? '2px solid' : '2px solid transparent',
          borderColor: unlocked ? 'success.light' : 'transparent',
          transition: 'all 0.2s',
          cursor: 'default',
          position: 'relative',
          '&:hover': unlocked ? { transform: 'translateY(-2px)', boxShadow: 6 } : {},
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 2, px: 1.5 }}>
          <Typography fontSize="2.2rem" lineHeight={1} mb={0.5}>
            {icon}
          </Typography>
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
            {name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
            {description}
          </Typography>
          {unlocked && unlockedDate && (
            <Typography variant="caption" color="success.main" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.5, fontWeight: 600 }}>
              {unlockedDate}
            </Typography>
          )}
          {unlocked && (
            <Box sx={{ mt: 0.5 }}>
              <Tooltip title={t('achievements.share')} arrow>
                <IconButton size="small" onClick={handleShare} disabled={sharing} sx={{ color: 'primary.main' }}>
                  {sharing ? <CircularProgress size={14} /> : <ShareIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </CardContent>
      </Card>
    </Tooltip>
  );
}

export default function AchievementsPage() {
  const { t } = useTranslation();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiService
      .get('/api/v1/achievements/my')
      .then((data) => setAchievements(Array.isArray(data) ? data : []))
      .catch(() => setAchievements([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;

  const unlockedCount = achievements.filter((a) => a.unlocked).length;
  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    acc[cat] = achievements.filter((a) => a.category === cat);
    return acc;
  }, {});

  const categoryLabel = {
    SCORE: t('achievements.categoryScore'),
    DAYS: t('achievements.categoryDays'),
    STREAK: t('achievements.categoryStreak'),
    COMPETITION: t('achievements.categoryCompetition'),
    WORDS: t('achievements.categoryWords'),
  };

  return (
    <Box className="page-content" sx={{ gap: 2, maxWidth: 600, mx: 'auto', width: '100%' }}>
      <Typography
        variant="h5"
        fontWeight={700}
        textAlign="center"
        color="white"
        sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
      >
        {t('achievements.title')}
      </Typography>

      <Chip
        label={t('achievements.progress', { unlocked: unlockedCount, total: achievements.length })}
        color="primary"
        sx={{ alignSelf: 'center', fontWeight: 700 }}
      />

      {CATEGORY_ORDER.map((cat) => {
        const items = grouped[cat];
        if (!items || items.length === 0) return null;
        return (
          <Box key={cat} sx={{ width: '100%' }}>
            <Typography variant="subtitle1" fontWeight={700} color="white" sx={{ mb: 1, textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
              {categoryLabel[cat]}
            </Typography>
            <Grid container spacing={1.5}>
              {items.map((a) => (
                <Grid item xs={4} sm={3} key={a.code}>
                  <AchievementCard achievement={a} />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
}
